import React, { useState, useEffect, useCallback } from "react";
import PageLayout from "../../../../layouts/PageLayout";
import { Row, Col, Form, Button, Container, Alert } from "react-bootstrap";
import Select from "react-select";
import { Link } from "react-router-dom";
import axios from "axios";

export default function AssignMultiRole() {
    const [users, setUsers] = useState([]);
    const [availableSystemRoles, setAvailableSystemRoles] = useState([]);
    const [selectedUserOption, setSelectedUserOption] = useState(null);
    const [userData, setUserData] = useState(null);
    const [selectedRoles, setSelectedRoles] = useState([]);

    const [loadingUsers, setLoadingUsers] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [alert, setAlert] = useState(null);
    const [errors, setErrors] = useState([]);

    const fetchUsers = useCallback(async (searchQuery = "") => {
        setLoadingUsers(true);
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/admin/assign-multi-role`,
                {
                    params: { search: searchQuery },
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                    },
                }
            );

            if (response.data?.success) {
                setUsers(response.data.data.users || []);
                setAvailableSystemRoles(response.data.data.roles || []);
            }
        } catch (err) {
            setAlert({
                type: "danger",
                message: err.response?.data?.message || "Failed to load users list.",
            });
        } finally {
            setLoadingUsers(false);
        }
    }, []);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const parseRoles = (roleString) => {
        if (!roleString) return [];
        return roleString
            .split(",")
            .map((r) => r.trim())
            .filter((r) => r.length > 0);
    };

    const handleUserSelect = (selectedOption) => {
        setSelectedUserOption(selectedOption);
        setAlert(null);
        setErrors([]);

        if (!selectedOption) {
            setUserData(null);
            setSelectedRoles([]);
            return;
        }

        const user = selectedOption.user;
        setUserData(user);

        const currentRoles = parseRoles(user.role);
        const initialSelected = currentRoles.map((r) => ({
            value: r,
            label: r.toUpperCase(),
        }));

        setSelectedRoles(initialSelected);
    };

    const handleRemoveRole = (roleToRemove) => {
        setSelectedRoles((prev) => prev.filter((r) => r.value !== roleToRemove));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!userData) return;

        setSubmitting(true);
        setAlert(null);
        setErrors([]);

        const roleValues = selectedRoles.map((r) => r.value);

        try {
            const response = await axios.put(
                `${process.env.REACT_APP_API_URL}/admin/assign-multi-role/${userData.id}`,
                { roles: roleValues },
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                    },
                }
            );

            if (response.data?.success) {
                const updatedUser = response.data.data;

                setAlert({
                    type: "success",
                    message: response.data.message || "User roles updated successfully.",
                });

                setUserData((prev) => ({
                    ...prev,
                    role: updatedUser.role,
                }));

                const updatedRoles = (updatedUser.roles || []).map((r) => ({
                    value: r,
                    label: r.toUpperCase(),
                }));
                setSelectedRoles(updatedRoles);

                setUsers((prev) =>
                    prev.map((u) => (u.id === updatedUser.id ? { ...u, role: updatedUser.role } : u))
                );
            }
        } catch (err) {
            if (err.response?.status === 422) {
                const validationErrors = err.response.data.errors;
                if (validationErrors?.roles) {
                    setErrors(validationErrors.roles);
                } else {
                    setAlert({
                        type: "danger",
                        message: "Validation failed. Check selected roles.",
                    });
                }
            } else {
                setAlert({
                    type: "danger",
                    message: err.response?.data?.message || "Failed to update roles.",
                });
            }
        } finally {
            setSubmitting(false);
        }
    };

    const userOptions = users.map((u) => {
        const nicknameText = u.nickname ? `(${u.nickname})` : "";
        return {
            value: u.id,
            label: `${u.name} ${nicknameText} - ${u.email}`,
            user: u,
        };
    });

    const roleDropdownOptions = availableSystemRoles
        .filter((role) => !selectedRoles.some((s) => s.value === role))
        .map((role) => ({
            value: role,
            label: String(role).toUpperCase(),
        }));

    return (
        <PageLayout>
            <Container fluid className="px-0">
                <Row>
                    <Col xl={12}>
                        <div className="mc-card mb-4">
                            <div className="mc-breadcrumb">
                                <h3 className="mc-breadcrumb-title">Assign Multi-Roles</h3>
                                <ul className="mc-breadcrumb-list">
                                    <li className="mc-breadcrumb-item">
                                        <Link to="#" className="mc-breadcrumb-link">
                                            Home
                                        </Link>
                                    </li>
                                    <li className="mc-breadcrumb-item">
                                        <Link to="#" className="mc-breadcrumb-link">
                                            Admin
                                        </Link>
                                    </li>
                                    <li className="mc-breadcrumb-item">Assign Roles</li>
                                </ul>
                            </div>
                        </div>
                    </Col>
                </Row>

                {alert && (
                    <Row>
                        <Col lg={12}>
                            <Alert
                                variant={alert.type}
                                dismissible
                                onClose={() => setAlert(null)}
                                className="mb-4"
                            >
                                {alert.message}
                            </Alert>
                        </Col>
                    </Row>
                )}

                <Form onSubmit={handleSubmit}>
                    <Row>
                        <Col lg={8}>
                            <div className="mc-card mb-4">
                                <h4 className="card-title mb-4">User Selection & Role Assignment</h4>

                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        Select User <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Select
                                        isClearable
                                        isSearchable
                                        options={userOptions}
                                        value={selectedUserOption}
                                        onChange={handleUserSelect}
                                        onInputChange={(val, action) => {
                                            if (action.action === "input-change") {
                                                fetchUsers(val);
                                            }
                                        }}
                                        placeholder="Search by name, nickname or email..."
                                        isLoading={loadingUsers}
                                        className={errors.length > 0 ? "is-invalid" : ""}
                                    />
                                </Form.Group>

                                {userData && (
                                    <>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Currently Assigned Roles</Form.Label>
                                            <div className="d-flex flex-wrap gap-2 p-3 border rounded bg-light align-items-center">
                                                {selectedRoles.length > 0 ? (
                                                    selectedRoles.map((role) => (
                                                        <span
                                                            key={role.value}
                                                            className="badge bg-primary rounded-pill px-3 py-2 d-inline-flex align-items-center gap-2"
                                                            style={{ fontSize: "12px", letterSpacing: "0.5px" }}
                                                        >
                                                            {role.label}
                                                            <button
                                                                type="button"
                                                                className="btn-close btn-close-white"
                                                                style={{ width: "0.4em", height: "0.4em" }}
                                                                onClick={() => handleRemoveRole(role.value)}
                                                            />
                                                        </span>
                                                    ))
                                                ) : (
                                                    <span className="text-muted small">
                                                        No roles currently assigned. Add one below.
                                                    </span>
                                                )}
                                            </div>

                                            {errors.length > 0 && (
                                                <div className="text-danger small mt-1">
                                                    {errors.map((err, idx) => (
                                                        <div key={idx}>{err}</div>
                                                    ))}
                                                </div>
                                            )}
                                        </Form.Group>

                                        <Form.Group className="mb-3">
                                            <Form.Label>Add Additional Role</Form.Label>
                                            <Select
                                                isSearchable
                                                value={null}
                                                options={roleDropdownOptions}
                                                onChange={(selected) => {
                                                    if (selected) {
                                                        setSelectedRoles((prev) => [...prev, selected]);
                                                    }
                                                }}
                                                placeholder={
                                                    roleDropdownOptions.length > 0
                                                        ? "Select role to add..."
                                                        : "All available roles are assigned"
                                                }
                                                isDisabled={roleDropdownOptions.length === 0}
                                            />
                                        </Form.Group>
                                    </>
                                )}
                            </div>
                        </Col>

                        <Col lg={4}>
                            <div className="mc-card mb-4">
                                <h4 className="card-title mb-4">Selected User Overview</h4>
                                {userData ? (
                                    <div>
                                        <div className="mb-3">
                                            <span className="text-muted small d-block mb-1">Full Name</span>
                                            <p className="fw-bold mb-0">{userData.name || "N/A"}</p>
                                        </div>
                                        <div className="mb-3">
                                            <span className="text-muted small d-block mb-1">Nickname</span>
                                            <p className="fw-bold mb-0">{userData.nickname || "N/A"}</p>
                                        </div>
                                        <div className="mb-3">
                                            <span className="text-muted small d-block mb-1">Email Address</span>
                                            <p className="fw-bold text-break mb-0">{userData.email}</p>
                                        </div>

                                        <hr className="my-3" />

                                        <Button
                                            type="submit"
                                            className="btn btn-primary w-100 mb-2"
                                            disabled={submitting || selectedRoles.length === 0}
                                        >
                                            {submitting ? "Saving Changes..." : "Save Role Changes"}
                                        </Button>

                                        <Button
                                            type="button"
                                            variant="secondary"
                                            className="w-100"
                                            onClick={() => handleUserSelect(null)}
                                            disabled={submitting}
                                        >
                                            Reset Selection
                                        </Button>
                                    </div>
                                ) : (
                                    <p className="text-muted small mb-0 text-center py-3">
                                        Please select a user from the form to view their details and modify assigned roles.
                                    </p>
                                )}
                            </div>
                        </Col>
                    </Row>
                </Form>
            </Container>
        </PageLayout>
    );
}